import { useEffect, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import { api, errText } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Users, Plus, Pencil, Trash2, RefreshCw, Mail } from "lucide-react";

const empty = { email: "", password: "", name: "", role: "salesperson", outlet_ids: [], ls_user_id: "", active: true };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/users"), api.get("/store/outlets")])
      .then(([u, o]) => { setUsers(u.data.users || []); setOutlets(o.data.outlets || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const syncFromLightspeed = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/users/sync");
      setSyncResult(r.data);
      const { created, updated, disabled } = r.data;
      toast.success(`Synced — ${created} new, ${updated} updated${disabled ? `, ${disabled} disabled` : ""}`);
      // Live-refresh outlets so newly added Lightspeed stores show without a restart.
      const o = await api.get("/store/outlets?refresh=true");
      setOutlets(o.data.outlets || []);
      const u = await api.get("/users");
      setUsers(u.data.users || []);
    } catch (e) {
      toast.error(errText(e.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const sendCredentials = async (u) => {
    setSendingId(u.id);
    try {
      const r = await api.post(`/users/${u.id}/send-credentials`);
      toast.success(`Sign-in details emailed to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(errText(e.response?.data?.detail) || "Could not send credentials");
    } finally {
      setSendingId(null);
    }
  };

  const emailUsers = users.filter((u) => u.email);
  const allSelected = emailUsers.length > 0 && selected.length === emailUsers.length;
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleSelectAll = () => setSelected(allSelected ? [] : emailUsers.map((u) => u.id));
  const selectUnsent = () => setSelected(emailUsers.filter((u) => !u.credentials_sent).map((u) => u.id));

  const sendBulk = async () => {
    if (!selected.length) return;
    setBulkSending(true);
    try {
      const r = await api.post("/users/send-credentials-bulk", { user_ids: selected });
      const { sent, skipped_no_email, failed } = r.data;
      toast.success(
        `Sign-in details sent to ${sent} user${sent === 1 ? "" : "s"}` +
        `${skipped_no_email ? `, ${skipped_no_email} skipped (no email)` : ""}` +
        `${failed ? `, ${failed} failed` : ""}`
      );
      setSelected([]);
    } catch (e) {
      toast.error(errText(e.response?.data?.detail) || "Bulk send failed");
    } finally {
      setBulkSending(false);
    }
  };

  const outletName = (id) => outlets.find((o) => o.id === id)?.name || id.slice(0, 6);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email || "", password: "", name: u.name, role: u.role, outlet_ids: u.outlet_ids || [], ls_user_id: u.ls_user_id || "", active: u.active !== false });
    setOpen(true);
  };

  const toggleOutlet = (id) => {
    setForm((f) => ({
      ...f,
      outlet_ids: f.outlet_ids.includes(id) ? f.outlet_ids.filter((x) => x !== id) : [...f.outlet_ids, id],
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, form);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail) || "Could not save user");
    } finally {
      setBusy(false);
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail) || "Could not delete");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <main className="mx-auto max-w-[1400px] px-6 py-6">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-400" />
            <h1 className="font-head text-2xl font-600 tracking-tight text-slate-900">Salespeople &amp; access</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={openNew} data-testid="add-user-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4" /> Add user
            </Button>
            <Button onClick={syncFromLightspeed} disabled={syncing} data-testid="sync-lightspeed-btn" variant="outline" className="gap-1.5">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync from Lightspeed
            </Button>
            <Button onClick={sendBulk} disabled={!selected.length || bulkSending} data-testid="send-bulk-btn" variant="outline" className="gap-1.5">
              {bulkSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send sign-in details{selected.length ? ` (${selected.length})` : ""}
            </Button>
            <Button onClick={selectUnsent} variant="ghost" size="sm" data-testid="select-unsent-btn" className="text-slate-500">
              Select not-yet-sent (with email)
            </Button>
          </div>
        </div>

        {syncResult && (
          <div className="mb-4 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="sync-result">
            <span>
              Lightspeed sync complete — <b>{syncResult.created}</b> new, <b>{syncResult.updated}</b> updated,{" "}
              <b>{syncResult.disabled || 0}</b> disabled, <b>{syncResult.skipped || 0}</b> skipped (of {syncResult.total} Lightspeed users).
            </span>
            <button onClick={() => setSyncResult(null)} className="ml-4 text-blue-500 hover:text-blue-700" data-testid="dismiss-sync-result">Dismiss</button>
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-8">
                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-users" />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Store access</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sign-in</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.email || u.username}`}>
                    <TableCell>
                      {u.email ? (
                        <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggleSelect(u.id)} data-testid={`select-user-${u.email}`} />
                      ) : null}
                    </TableCell>
                    <TableCell className="font-500 text-slate-900">{u.name}</TableCell>
                    <TableCell className="text-slate-600">{u.username || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-slate-600">{u.email || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={u.role === "admin" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}>
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[280px] text-xs text-slate-500">
                      {u.role === "admin" || u.unrestricted_outlets ? "All stores" : (u.outlet_ids?.length ? u.outlet_ids.map(outletName).join(", ") : "— none —")}
                    </TableCell>
                    <TableCell>
                      {u.active !== false ? (
                        <span className="text-xs text-green-700">Active</span>
                      ) : (
                        <span className="text-xs text-slate-400">Disabled</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`creds-status-${u.email || u.username}`}>
                      {u.credentials_sent ? (
                        <span className="text-green-700">Sent{u.credentials_sent_at ? ` · ${new Date(u.credentials_sent_at).toLocaleDateString()}` : ""}</span>
                      ) : (
                        <span className="text-slate-400">Not sent</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {u.email && (
                          <button onClick={() => sendCredentials(u)} disabled={sendingId === u.id} data-testid={`send-credentials-${u.email}`} title="Email sign-in details" className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600 disabled:opacity-50">
                            {sendingId === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                          </button>
                        )}
                        <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.email || u.username}`} className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => del(u)} data-testid={`delete-user-${u.email || u.username}`} className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head">{editing ? "Edit user" : "Add user"}</DialogTitle>
            <DialogDescription>Each user sees quotes and history for the stores you grant them below. Admins see all stores.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email {editing && <span className="text-slate-400">(editable — used for login &amp; sign-in emails)</span>}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@nzbeds.co.nz" data-testid="user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Password {editing && <span className="text-slate-400">(leave blank to keep)</span>}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="salesperson">Salesperson</SelectItem>
                  <SelectItem value="admin">Admin (all stores)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "salesperson" && (
              <div className="space-y-2">
                <Label>Store access</Label>
                <div className="max-h-44 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 p-3">
                  {outlets.map((o) => (
                    <label key={o.id} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                      <Checkbox checked={form.outlet_ids.includes(o.id)} onCheckedChange={() => toggleOutlet(o.id)} data-testid={`outlet-${o.name}`} />
                      {o.name}
                    </label>
                  ))}
                  {outlets.length === 0 && <span className="text-xs text-slate-400">No outlets loaded.</span>}
                </div>
              </div>
            )}
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="user-active" />
              Account active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="save-user-btn" className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
